// src/modules/whatsapp-app/api.ts
//
// Cliente de la app de WhatsApp (Orión):
//   /admin/whatsapp/app/*            números, modo de respuesta, perfil, catálogo, plantillas, horario
//   /threads/{id}/messages           conversación (autor, sugerencia, texto limpio, media, ventana 24 h)
//   /threads/{id}/human-message      enviar como persona (reusa sendHumanMessage del SDK)
//   /threads/{id}/suggestion/dismiss · /reply-mode · /read · /template
// La org la pone el backend desde el JWT: aquí nunca se manda org_id.

import { apiUrl } from '@/config';
import { ensureFreshToken } from '@/lib/tokenRefresh';
import type { InboxItem, ThreadDTO } from '@/lib/api';

export type ReplyMode = 'auto' | 'suggest' | 'off';

export const MODE_LABEL: Record<ReplyMode, string> = {
  auto: 'Automático',
  suggest: 'Solo sugiere',
  off: 'Apagado',
};

export const MODE_HINT: Record<ReplyMode, string> = {
  auto: 'El agente responde solo a tus clientes.',
  suggest: 'El agente escribe la respuesta y tú decides: enviarla, editarla o descartarla.',
  off: 'El agente no participa. Solo tu equipo responde.',
};

export class WaApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  await ensureFreshToken();
  const token = localStorage.getItem('auth_token') || '';
  const res = await fetch(apiUrl(path), {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
      ...(init?.headers || {}),
    },
  });
  const ct = res.headers.get('content-type') || '';
  const body = ct.includes('application/json') ? await res.json().catch(() => ({})) : await res.text();
  if (!res.ok) {
    const detail = typeof body === 'object' && body ? (body as any).detail : null;
    throw new WaApiError(
      typeof detail === 'string' ? detail : `No se pudo completar (HTTP ${res.status})`,
      res.status,
    );
  }
  return body as T;
}

/* ── Números + modo ─────────────────────────────────────────────── */

export interface WaNumber {
  key: string;
  provider: 'meta' | 'twilio';
  phone_number_id: string | null;
  waba_id: string | null;
  display_name: string | null;
  phone: string | null;
  is_default: boolean;
  enabled: boolean;
  reply_mode: ReplyMode | null;      // propio del número (null = hereda)
  effective_mode: ReplyMode;
}

export interface WaOverview {
  org_id: string;
  org_mode: ReplyMode;
  org_mode_configured: boolean;
  numbers: WaNumber[];
  connected: boolean;
  can_edit: boolean;
  last_change: { by: string | null; at: string; scope: string; to: string } | null;
}

export const getOverview = () => call<WaOverview>('/admin/whatsapp/app/overview');

export const setReplyMode = (reply_mode: ReplyMode | null, number_key?: string | null) =>
  call<WaOverview>('/admin/whatsapp/app/reply-mode', {
    method: 'PUT',
    body: JSON.stringify({ reply_mode, number_key: number_key ?? null }),
  });

export interface WaNumberStatus {
  key: string;
  provider: string;
  live: boolean;
  enabled: boolean;
  display_phone_number?: string | null;
  verified_name?: string | null;
  quality_rating?: string | null;
  code_verification_status?: string | null;
  name_status?: string | null;
  status?: string | null;
  messaging_limit_tier?: string | null;
  phone?: string | null;
  error?: string;
  nota?: string;
}

export const getNumberStatus = (key: string) =>
  call<WaNumberStatus>(`/admin/whatsapp/app/numbers/${encodeURIComponent(key)}/status`);

/* ── Chats ──────────────────────────────────────────────────────── */

export type WaChat = InboxItem & {
  last_customer_at?: string | null;
  window_open?: boolean;
  awaiting_reply?: boolean;
};

export const getChats = (search?: string) =>
  call<{ ok: boolean; items: WaChat[] }>(
    `/admin/whatsapp/app/chats?limit=200${search ? `&search=${encodeURIComponent(search)}` : ''}`,
  );

export interface WaMedia { tipo: 'imagen' | 'audio' | 'video' | 'documento'; url: string }

export interface WaMessage {
  id: string;
  role: 'user' | 'assistant';
  at: string;
  bloques: { tipo: string; contenido: string }[];
  author?: 'customer' | 'agent' | 'team' | 'automation' | null;
  author_name?: string | null;
  suggestion?: boolean;
  suggestion_state?: 'sent' | 'edited' | 'dismissed' | 'superseded' | null;
  text?: string | null;
  media?: WaMedia[] | null;
}

export interface WaThreadState {
  last_customer_at: string | null;
  window_open: boolean;
  window_expires_at: string | null;
  pending_suggestion: { text: string; at?: string | null; source?: string | null } | null;
  reply_mode: ReplyMode | null;
  effective_mode: ReplyMode;
  configured_mode: ReplyMode;
  mode_source: 'pause' | 'thread' | 'number' | 'org' | 'default' | string;
  wa_number: string | null;
}

export const getMessages = (threadId: string) =>
  call<{ ok: boolean; messages: WaMessage[]; thread?: { whatsapp?: WaThreadState } }>(
    `/threads/${threadId}/messages?limit=200&merge_person=true`,
  );

export const sendText = (threadId: string, text: string, fromSuggestion = false) =>
  call<{ ok: boolean }>(`/threads/${threadId}/human-message`, {
    method: 'POST',
    body: JSON.stringify({ text, from_suggestion: fromSuggestion }),
  });

export const dismissSuggestion = (threadId: string) =>
  call<ThreadDTO>(`/threads/${threadId}/suggestion/dismiss`, { method: 'POST' });

export const setThreadMode = (threadId: string, reply_mode: ReplyMode | null) =>
  call<ThreadDTO>(`/threads/${threadId}/reply-mode`, {
    method: 'PUT',
    body: JSON.stringify({ reply_mode }),
  });

export const markRead = (threadId: string) =>
  call<ThreadDTO>(`/threads/${threadId}/read`, { method: 'POST' });

/* ── Plantillas, perfil, catálogo, horario ─────────────────────── */

export interface WaTemplate {
  name: string;
  language: string;
  category: string | null;
  body: string;
  header: string | null;
  variables: number;
  sendable: boolean;
  why_not: string | null;
  provider: 'meta' | 'twilio';
  ref: string;
}

export const getTemplates = () =>
  call<{ provider: string | null; templates: WaTemplate[]; motivo?: string }>('/admin/whatsapp/app/templates');

export const sendTemplate = (threadId: string, name: string, language: string, variables: string[]) =>
  call<{ ok: boolean }>(`/threads/${threadId}/template`, {
    method: 'POST',
    body: JSON.stringify({ name, language, variables }),
  });

export interface WaProfile {
  about?: string | null;
  address?: string | null;
  description?: string | null;
  email?: string | null;
  websites?: string[] | null;
  vertical?: string | null;
  profile_picture_url?: string | null;
}

export const getProfile = (numberKey?: string | null) =>
  call<{ available: boolean; motivo?: string; number_key?: string; profile?: WaProfile }>(
    `/admin/whatsapp/app/profile${numberKey ? `?number_key=${encodeURIComponent(numberKey)}` : ''}`,
  );

export const saveProfile = (numberKey: string | null, profile: WaProfile) =>
  call<{ available: boolean; profile?: WaProfile }>('/admin/whatsapp/app/profile', {
    method: 'PUT',
    body: JSON.stringify({ number_key: numberKey, ...profile }),
  });

export interface WaCatalogItem {
  id: string;
  retailer_id?: string | null;
  name?: string | null;
  price?: string | null;
  currency?: string | null;
  image_url?: string | null;
  availability?: string | null;
  review_status?: string | null;
  url?: string | null;
}

export const getCatalog = (numberKey?: string | null) =>
  call<{ available: boolean; motivo?: string; catalog?: { id: string; name: string } | null;
    items?: WaCatalogItem[]; more?: boolean; error?: string }>(
    `/admin/whatsapp/app/catalog${numberKey ? `?number_key=${encodeURIComponent(numberKey)}` : ''}`,
  );

export const getHours = () =>
  call<{ tz: string; start: string; end: string; days: number[]; uso: string;
    mensaje_fuera_de_horario: string | null }>('/admin/whatsapp/app/hours');

/* ── Utilidades de presentación ─────────────────────────────────── */

/** Variables {{1}}…{{n}} de un cuerpo de plantilla. */
export function renderTemplate(body: string, vars: string[]): string {
  return body.replace(/\{\{\s*(\d+)\s*\}\}/g, (_, n) => vars[Number(n) - 1] || `{{${n}}}`);
}

export function numberLabel(n: Pick<WaNumber, 'display_name' | 'phone' | 'phone_number_id' | 'provider'>,
  live?: WaNumberStatus | null): string {
  return live?.display_phone_number || n.phone || n.display_name || (n.provider === 'meta' ? 'WhatsApp Cloud' : 'Twilio');
}
